import { Stack } from '@landler/component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { BenchmarkType, PlotReportFactElement, R1FactType } from '@/api/rest/resources/types/fact';
import { BenchmarkExplainerType } from '@/api/rest/resources/types/widget';
import { ImpactProjectionsLink } from '@/components/ImpactProjectionsLink';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useBenchmarkForFact, useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import { assertBenchmarkType } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting';

import { kgToTonneFormatter } from './Charts/utils';
import { BenchmarkPill } from './Pill/BenchmarkPill';

type BenchmarkExplainerProps = {
  benchmarkFactType: R1FactType;
  latestAnalysis: PlotReportFactElement<number | null> | undefined;
  benchmarkExplainerType?: BenchmarkExplainerType;
  showProjectionsLink?: boolean;
  oneYearTrend: PlotReportFactElement<number | null> | undefined;
};

export const BenchmarkExplainer = ({
  benchmarkExplainerType,
  benchmarkFactType,
  latestAnalysis,
  showProjectionsLink,
  oneYearTrend,
}: BenchmarkExplainerProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();

  const formattedLatestAnalysis = formatFactKilogramsToTonnes(latestAnalysis);

  let oneYearTrendDisplay = `${useDisplayNumber(Number(oneYearTrend?.value ?? 0), { signDisplay: 'always' })} %`;
  oneYearTrendDisplay = !isNullish(oneYearTrend) ? oneYearTrendDisplay : '-';

  const benchmarks = useBenchmarksForOrganization(benchmarkFactType, null, kgToTonneFormatter, undefined);
  const benchmarkType = useBenchmarkForFact(benchmarks, formattedLatestAnalysis?.value);

  const benchmarkExplainer = useExplainerKeyByType(benchmarkType, benchmarkExplainerType, oneYearTrendDisplay);
  return (
    <>
      {benchmarkExplainer && (
        <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          {benchmarkType && <BenchmarkPill benchmarkType={benchmarkType} />}
          <span className='typography-body1Bold'>{t('global.whatDoesThisMean')}</span>
          <span className='typography-body1 text-text-secondary whitespace-pre-wrap'>{benchmarkExplainer}</span>
          {showProjectionsLink && projectId && <ImpactProjectionsLink />}
        </Stack>
      )}
    </>
  );
};

const useExplainerKeyByType = (
  benchmarkType?: BenchmarkType | null,
  type?: BenchmarkExplainerType,
  trendPercent?: string,
) => {
  const { t } = useTranslation();

  if (type === BenchmarkExplainerType.canopy_cover) {
    return t(`shared.ncaDetail.details.forest.explainers.canopyCoverDetails.body`);
  }

  if (type === BenchmarkExplainerType.canopy_height) {
    return t(`shared.ncaDetail.details.forest.explainers.canopyHeightDetails.body`);
  }

  if (isNullish(benchmarkType)) return '';

  assertBenchmarkType(benchmarkType, type ?? 'unknown');
  assertBenchmarkType(benchmarkType, type ?? 'unknown', BenchmarkType.unchanged);
  assertBenchmarkType(benchmarkType, type ?? 'unknown', BenchmarkType.stable);
  assertBenchmarkType(benchmarkType, type ?? 'unknown', BenchmarkType.alerted);

  switch (type) {
    case BenchmarkExplainerType.historic_below_ground_carbon:
      return t(`shared.ncaDetail.details.carbon.explainers.cbgBenchmark.${benchmarkType}`);
    case BenchmarkExplainerType.historic_water_soil_moisture:
      return t(`shared.ncaDetail.details.water.explainers.whcBenchmark.${benchmarkType}`, {
        trendPercent,
      });
    case BenchmarkExplainerType.protected_on_farm_habitat:
      return t(`shared.ncaDetail.details.biodiversity.explainers.bpBenchmark.${benchmarkType}`);
    default:
      return '';
  }
};
